import { useCallback } from 'react';
import type { OptionData } from '@atlaskit/user-picker';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { performGetRequest } from '@atlassian/jira-servicedesk-common/src/utils/fetch/requests.tsx';
import messages from './messages.tsx';
import {
	CUSTOMER_ORGANIZATION_ARI_PREFIX,
	GROUP_ARI_PREFIX,
	USER_ARI_PREFIX,
	type CustomerOrganization,
	type GroupUserPickerResponse,
} from './types.tsx';

const DEBOUNCE_DELAY_MS = 300;

export const useSearchPrincipals = () => {
	const { formatMessage } = useIntl();

	const usersAndGroupsRequest = useCallback(
		(query: string) =>
			performGetRequest(
				`/rest/api/3/groupuserpicker?query=${encodeURIComponent(query)}&maxResults=50&showAvatar=true&avatarSize=medium&caseInsensitive=true`,
			),
		[],
	);
	const customerOrganizationsRequest = useCallback(
		(query: string) =>
			performGetRequest(
				`/rest/servicedesk/1/organisations/search?query=${encodeURIComponent(query)}`,
			),
		[],
	);

	const {
		data: usersAndGroupsData,
		fetch: fetchUsersAndGroups,
		loading: usersAndGroupsLoading,
		error: usersAndGroupsError,
	} = useService<GroupUserPickerResponse, string>(usersAndGroupsRequest);
	const {
		data: customerOrganizationsData,
		fetch: fetchCustomerOrganizations,
		loading: customerOrganizationsLoading,
		error: customerOrganizationsError,
	} = useService<CustomerOrganization[], string>(customerOrganizationsRequest);
	const [debouncedFetch] = useDebouncedCallback(
		(query) => Promise.all([fetchUsersAndGroups(query), fetchCustomerOrganizations(query)]),
		DEBOUNCE_DELAY_MS,
	);

	const userOptions: OptionData[] =
		usersAndGroupsData?.users.users.map((user) => ({
			id: `${USER_ARI_PREFIX}/${user.accountId}`,
			name: user.displayName,
			type: 'user',
			avatarUrl: user.avatarUrl,
		})) ?? [];

	const groupOptions: OptionData[] =
		usersAndGroupsData?.groups.groups.map((group) => ({
			id: `${GROUP_ARI_PREFIX}/${group.groupId}`,
			name: group.name,
			type: 'group',
			byline: formatMessage(messages.group),
		})) ?? [];

	const customerOrganizationOptions: OptionData[] =
		customerOrganizationsData?.map((customerOrganization) => ({
			id: `${CUSTOMER_ORGANIZATION_ARI_PREFIX}/${customerOrganization.externalId}`,
			name: customerOrganization.name,
			type: 'group',
			byline: formatMessage(messages.customerOrganization),
		})) ?? [];

	const options = userOptions
		.concat(groupOptions)
		.concat(customerOrganizationOptions)
		.sort((a, b) => a.name.localeCompare(b.name));

	const loading = usersAndGroupsLoading || customerOrganizationsLoading;
	const error = usersAndGroupsError || customerOrganizationsError;
	const data = loading || error ? [] : options;

	return { data, fetch: debouncedFetch, loading, error };
};
